import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ref, get, set, onValue, push } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Puzzle.css';

const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⌫']
];

// Create Puzzle Form Component
const CreatePuzzleForm = ({ onClose, onSave, currentUser, userProfile }) => {
  const [title, setTitle] = useState('');
  const [completionMessage, setCompletionMessage] = useState('');
  const [hint, setHint] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // Each group has a unique id for animation tracking
  const [groups, setGroups] = useState([
    { id: 'group-1', category: '', words: ['', '', '', ''] },
    { id: 'group-2', category: '', words: ['', '', '', ''] },
    { id: 'group-3', category: '', words: ['', '', '', ''] },
    { id: 'group-4', category: '', words: ['', '', '', ''] }
  ]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const updateGroup = (groupIndex, field, value) => {
    const newGroups = [...groups];
    newGroups[groupIndex][field] = value;
    setGroups(newGroups);
    setErrorMessage(''); // Clear error when user makes changes
  };

  const updateWord = (groupIndex, wordIndex, value) => {
    const newGroups = [...groups];
    newGroups[groupIndex].words[wordIndex] = value.toUpperCase();
    setGroups(newGroups);
    setErrorMessage(''); // Clear error when user makes changes
  };

  // Move group up (easier difficulty)
  const moveGroupUp = (index) => {
    if (index === 0) return;
    const newGroups = [...groups];
    [newGroups[index - 1], newGroups[index]] = [newGroups[index], newGroups[index - 1]];
    setGroups(newGroups);
  };

  // Move group down (harder difficulty)
  const moveGroupDown = (index) => {
    if (index === groups.length - 1) return;
    const newGroups = [...groups];
    [newGroups[index], newGroups[index + 1]] = [newGroups[index + 1], newGroups[index]];
    setGroups(newGroups);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newGroups = [...groups];
    const [draggedGroup] = newGroups.splice(draggedIndex, 1);
    newGroups.splice(dropIndex, 0, draggedGroup);
    setGroups(newGroups);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleSubmit = () => {
    // Validate all fields are filled
    if (!title.trim()) {
      setErrorMessage('Please enter a puzzle title');
      return;
    }

    for (let i = 0; i < groups.length; i++) {
      if (!groups[i].category.trim()) {
        setErrorMessage(`Please enter a category for group ${i + 1}`);
        return;
      }
      for (let j = 0; j < groups[i].words.length; j++) {
        if (!groups[i].words[j].trim()) {
          setErrorMessage(`Please fill all words in group ${i + 1}`);
          return;
        }
      }
    }

    // Check for duplicate words
    const allWords = groups.flatMap(g => g.words);
    const uniqueWords = new Set(allWords);
    if (uniqueWords.size !== allWords.length) {
      setErrorMessage('Each word must be unique! Please check for duplicates.');
      return;
    }

    setErrorMessage('');
    onSave({
      title,
      completionMessage,
      hint,
      groups
    });
  };

  return (
    <motion.div
      className="create-puzzle-form"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="form-header">
        <h3>Create New Puzzle</h3>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>

      <div className="form-content">
        <div className="form-group">
          <label>Puzzle Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., 'Our Favorite Things'"
            maxLength={50}
          />
        </div>

        <div className="form-group">
          <label>Completion Message (Optional)</label>
          <input
            type="text"
            value={completionMessage}
            onChange={(e) => setCompletionMessage(e.target.value)}
            placeholder="e.g., 'You know me so well!'"
            maxLength={100}
          />
        </div>

        <div className="form-group">
          <label>Hint (Optional)</label>
          <input
            type="text"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="e.g., 'Think about our favorite movies...'"
            maxLength={150}
          />
          <span className="form-hint-text">Shown after a failed attempt to help your partner</span>
        </div>

        {errorMessage && (
          <motion.div
            className="form-error-message"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {errorMessage}
          </motion.div>
        )}

        <div className={`groups-section ${draggedIndex !== null ? 'is-dragging' : ''}`}>
          <h4>Create 4 Groups</h4>
          <p className="groups-hint">Drag groups or use arrows to reorder difficulty</p>
          <AnimatePresence mode="popLayout">
            {groups.map((group, groupIndex) => (
              <motion.div
                key={group.id}
                layout
                layoutId={group.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{
                  layout: { type: "spring", stiffness: 120, damping: 20, duration: 0.6 },
                  opacity: { duration: 0.3 }
                }}
                className={`group-form group-${groupIndex + 1} ${draggedIndex === groupIndex ? 'dragging' : ''} ${dragOverIndex === groupIndex ? 'drag-over' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, groupIndex)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, groupIndex)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, groupIndex)}
              >
                <div className="group-header">
                  <div className="group-header-left">
                    <span className="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <span className="group-number">Group {groupIndex + 1}</span>
                  </div>
                  <div className="group-header-center">
                    <span className="difficulty-label">
                      {['Easiest', 'Medium', 'Hard', 'Hardest'][groupIndex]}
                    </span>
                  </div>
                  <div className="group-header-right">
                    <button
                      type="button"
                      className="reorder-btn"
                      onClick={() => moveGroupUp(groupIndex)}
                      disabled={groupIndex === 0}
                      title="Move to easier difficulty"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="reorder-btn"
                      onClick={() => moveGroupDown(groupIndex)}
                      disabled={groupIndex === groups.length - 1}
                      title="Move to harder difficulty"
                    >
                      ↓
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  className="category-input"
                  value={group.category}
                  onChange={(e) => updateGroup(groupIndex, 'category', e.target.value)}
                  placeholder="Category (e.g., 'Things we love')"
                  maxLength={30}
                />
                <div className="words-grid">
                  {group.words.map((word, wordIndex) => (
                    <input
                      key={wordIndex}
                      type="text"
                      value={word}
                      onChange={(e) => updateWord(groupIndex, wordIndex, e.target.value)}
                      placeholder={`Word ${wordIndex + 1}`}
                      maxLength={15}
                    />
                  ))}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div className="form-actions">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button className="save-btn" onClick={handleSubmit}>Create Puzzle</button>
        </div>
      </div>
    </motion.div>
  );
};

const Puzzle = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('wordle');

  // Wordle state
  const [wordleWord, setWordleWord] = useState('');
  const [wordleGuesses, setWordleGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [wordleComplete, setWordleComplete] = useState(false);
  const [wordleWon, setWordleWon] = useState(false);
  const [partnerCompleted, setPartnerCompleted] = useState(false);
  const [partnerWon, setPartnerWon] = useState(false);
  const [partnerGuesses, setPartnerGuesses] = useState([]);
  const [partnerName, setPartnerName] = useState('Partner');
  const [showComparison, setShowComparison] = useState(false);
  const [wordleDate, setWordleDate] = useState('');
  const [keyboardColors, setKeyboardColors] = useState({});

  // Connections state
  const [connectionsPuzzles, setConnectionsPuzzles] = useState([]);
  const [selectedPuzzle, setSelectedPuzzle] = useState(null);
  const [selectedWords, setSelectedWords] = useState([]);
  const [solvedGroups, setSolvedGroups] = useState([]);
  const [remainingWords, setRemainingWords] = useState([]);
  const [mistakes, setMistakes] = useState(0);
  const [connectionsComplete, setConnectionsComplete] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [partnerAttempting, setPartnerAttempting] = useState(false);
  const [puzzleFilter, setPuzzleFilter] = useState('all'); // 'all', 'henry', 'omi', 'unattempted'
  const [hintRevealed, setHintRevealed] = useState(false);
  const [hasFailedBefore, setHasFailedBefore] = useState(false);

  // Get today's date string in EST timezone
  const getTodayString = () => {
    // Create date in EST timezone
    const estDate = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
    const today = new Date(estDate);
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  // Update keyboard colors based on guesses
  useEffect(() => {
    const colors = {};
    wordleGuesses.forEach(guess => {
      guess.split('').forEach((letter, index) => {
        if (letter === wordleWord[index]) {
          colors[letter] = 'correct';
        } else if (wordleWord.includes(letter) && colors[letter] !== 'correct') {
          colors[letter] = 'present';
        } else if (!colors[letter]) {
          colors[letter] = 'absent';
        }
      });
    });
    setKeyboardColors(colors);
  }, [wordleGuesses, wordleWord]);

  // Load today's Wordle
  useEffect(() => {
    if (!currentUser) return;

    const todayStr = getTodayString();
    setWordleDate(todayStr);

    // Use a ref-like object to store unsubscribe functions
    // This ensures cleanup has access to the latest values
    const subscriptions = { progress: null, partner: null };
    let isMounted = true;

    const loadTodayWordle = async () => {
      // Load word bucket
      const wordBucketRef = ref(database, 'puzzles/wordBucket');
      const wordBucketSnap = await get(wordBucketRef);

      if (!isMounted) return;

      if (!wordBucketSnap.exists()) {
        console.error('No word bucket found! Admin needs to add words.');
        return;
      }

      const wordBucketData = wordBucketSnap.val();
      const allWords = Object.values(wordBucketData).map(item => item.word);

      const puzzleRef = ref(database, `puzzles/wordle/${todayStr}`);
      const puzzleSnap = await get(puzzleRef);

      if (!isMounted) return;

      if (puzzleSnap.exists()) {
        setWordleWord(puzzleSnap.val().word);
      } else {
        // Get word usage history
        const usageRef = ref(database, 'puzzles/wordle/usedWords');
        const usageSnap = await get(usageRef);
        const usedWords = usageSnap.val() || {};

        // Find unused words
        const unusedWords = allWords.filter(word => !usedWords[word]);

        let selectedWord;
        if (unusedWords.length > 0) {
          // Pick random unused word
          selectedWord = unusedWords[Math.floor(Math.random() * unusedWords.length)];
        } else {
          // All words used, reset and pick any word
          await set(usageRef, {});
          selectedWord = allWords[Math.floor(Math.random() * allWords.length)];
        }

        if (!isMounted) return;

        // Save puzzle and mark word as used
        await set(puzzleRef, {
          word: selectedWord,
          createdAt: new Date().toISOString(),
        });

        // Mark word as used
        await set(ref(database, `puzzles/wordle/usedWords/${selectedWord}`), {
          date: todayStr,
          timestamp: new Date().toISOString(),
        });

        setWordleWord(selectedWord);
      }

      if (!isMounted) return;

      // Subscribe to current user's progress
      const progressRef = ref(database, `puzzles/wordle/${todayStr}/progress/${currentUser.uid}`);
      subscriptions.progress = onValue(progressRef, (snapshot) => {
        if (!isMounted) return;
        const progress = snapshot.val();
        if (progress) {
          setWordleGuesses(progress.guesses || []);
          setWordleComplete(progress.completed || false);
          setWordleWon(progress.won || false);
        }
      });

      // Get partner info and subscribe to their progress
      const usersRef = ref(database, 'users');
      const usersSnap = await get(usersRef);
      const users = usersSnap.val();

      if (!isMounted) return;

      if (users) {
        // Filter to only include actual user profiles
        // Legacy entries like "Admin", "Henry", "Omi" are keyed by name, not UID
        // Real user profiles have Firebase Auth UIDs which are ~28 character alphanumeric strings
        const otherUsers = Object.entries(users).filter(([uid, data]) =>
          uid !== currentUser.uid &&
          data &&
          uid.length > 20 && // Firebase UIDs are long alphanumeric strings
          (data.email || data.name) // Has some profile data
        );
        if (otherUsers.length > 0) {
          const [partnerUid, partnerData] = otherUsers[0];
          setPartnerName(partnerData.name || partnerData.displayName || 'Partner');

          // Subscribe to partner's progress with proper cleanup
          const partnerProgressRef = ref(database, `puzzles/wordle/${todayStr}/progress/${partnerUid}`);
          console.log('Setting up partner listener for:', partnerUid, 'at path:', `puzzles/wordle/${todayStr}/progress/${partnerUid}`);

          subscriptions.partner = onValue(partnerProgressRef, (snapshot) => {
            if (!isMounted) return;
            const partnerProgress = snapshot.val();
            console.log('Partner progress update received:', partnerProgress);
            if (partnerProgress) {
              setPartnerCompleted(partnerProgress.completed || false);
              setPartnerWon(partnerProgress.won || false);
              setPartnerGuesses(partnerProgress.guesses || []);
            } else {
              // Reset partner state if no progress exists yet
              setPartnerCompleted(false);
              setPartnerWon(false);
              setPartnerGuesses([]);
            }
          });
        }
      }
    };

    loadTodayWordle();

    // Cleanup function - unsubscribe from both listeners
    return () => {
      isMounted = false;
      if (subscriptions.progress) subscriptions.progress();
      if (subscriptions.partner) subscriptions.partner();
    };
  }, [currentUser]);

  // Load Connections puzzles
  useEffect(() => {
    if (!currentUser) return;

    const puzzlesRef = ref(database, 'puzzles/connections');
    // Fetch partner info once, then use snapshot data for progress (avoids async race conditions)
    const usersRef = ref(database, 'users');
    let partner = null;
    get(usersRef).then(usersSnap => {
      const allUsers = usersSnap.val();
      const usersList = Object.entries(allUsers || {})
        .filter(([uid, data]) => data && uid.length > 20 && (data.email || data.name))
        .map(([uid, data]) => ({ uid, ...data }));
      partner = usersList.find(u => u.uid !== currentUser.uid) || null;
    });

    const unsubscribe = onValue(puzzlesRef, (snapshot) => {
      const puzzles = snapshot.val();
      if (puzzles) {
        // Extract progress directly from snapshot data instead of separate get() calls
        const puzzleArray = Object.entries(puzzles).map(([id, data]) => {
          const { progress, ...puzzleData } = data;
          const myProgress = progress?.[currentUser.uid] || null;
          const partnerProgress = partner ? (progress?.[partner.uid] || null) : null;

          return {
            id,
            ...puzzleData,
            myProgress,
            partnerProgress,
            partnerName: partner?.name || partner?.displayName || 'Partner'
          };
        });
        // Sort puzzles by createdAt date, most recent first
        puzzleArray.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
        setConnectionsPuzzles(puzzleArray);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Check if both completed Wordle
  useEffect(() => {
    if (wordleComplete && partnerCompleted) {
      setShowComparison(true);
    }
  }, [wordleComplete, partnerCompleted]);

  // Handle Wordle guess submission
  const handleWordleGuess = async () => {
    if (currentGuess.length !== 5 || wordleComplete) return;

    const guess = currentGuess.toUpperCase();
    const newGuesses = [...wordleGuesses, guess];
    setWordleGuesses(newGuesses);
    setCurrentGuess('');

    const won = guess === wordleWord;
    const complete = won || newGuesses.length >= 6;

    if (complete) {
      setWordleComplete(true);
      setWordleWon(won);
    }

    const progressRef = ref(database, `puzzles/wordle/${wordleDate}/progress/${currentUser.uid}`);
    await set(progressRef, {
      guesses: newGuesses,
      completed: complete,
      won: won,
      updatedAt: new Date().toISOString(),
    });
  };

  // Get tile color based on letter position
  const getTileColor = (guess, index) => {
    const letter = guess[index];
    const targetLetter = wordleWord[index];

    if (letter === targetLetter) {
      return 'correct';
    } else if (wordleWord.includes(letter)) {
      return 'present';
    } else {
      return 'absent';
    }
  };

  // Handle keyboard input
  const handleKeyPress = (e) => {
    if (wordleComplete) return;

    if (e.key === 'Enter') {
      handleWordleGuess();
    } else if (e.key === 'Backspace') {
      setCurrentGuess(prev => prev.slice(0, -1));
    } else if (/^[a-zA-Z]$/.test(e.key) && currentGuess.length < 5) {
      setCurrentGuess(prev => (prev + e.key).toUpperCase());
    }
  };

  // Handle virtual keyboard click
  const handleKeyboardClick = (key) => {
    if (wordleComplete) return;

    if (key === 'ENTER') {
      handleWordleGuess();
    } else if (key === '⌫') {
      setCurrentGuess(prev => prev.slice(0, -1));
    } else if (currentGuess.length < 5) {
      setCurrentGuess(prev => (prev + key).toUpperCase());
    }
  };

  // Connections: Toggle word selection
  const toggleWordSelection = (word) => {
    if (selectedWords.includes(word)) {
      setSelectedWords(selectedWords.filter(w => w !== word));
    } else if (selectedWords.length < 4) {
      setSelectedWords([...selectedWords, word]);
    }
  };

  // Connections: Submit guess
  const handleConnectionsGuess = () => {
    if (selectedWords.length !== 4 || !selectedPuzzle) return;

    // Check if selection matches any group and get its index (difficulty level)
    let matchedGroup = null;

    selectedPuzzle.groups.forEach((group, index) => {
      if (group.words.every(word => selectedWords.includes(word)) &&
          selectedWords.every(word => group.words.includes(word))) {
        matchedGroup = { ...group, difficulty: index }; // Add difficulty level (0=easiest, 3=hardest)
      }
    });

    if (matchedGroup) {
      // Correct guess!
      setSolvedGroups([...solvedGroups, matchedGroup]);
      setRemainingWords(remainingWords.filter(w => !selectedWords.includes(w)));
      setSelectedWords([]);

      // Check if complete
      if (solvedGroups.length + 1 === selectedPuzzle.groups.length) {
        setConnectionsComplete(true);
        // Save completion with mistakes count
        const progressRef = ref(database, `puzzles/connections/${selectedPuzzle.id}/progress/${currentUser.uid}`);
        set(progressRef, {
          completed: true,
          mistakes: mistakes,
          solvedCount: solvedGroups.length + 1,
          completedAt: new Date().toISOString()
        });
      }
    } else {
      // Wrong guess
      setMistakes(mistakes + 1);
      setSelectedWords([]);

      if (mistakes + 1 >= 4) {
        setConnectionsComplete(true);
        // Save failed attempt with mistakes and progress
        const progressRef = ref(database, `puzzles/connections/${selectedPuzzle.id}/progress/${currentUser.uid}`);
        set(progressRef, {
          completed: true,
          failed: true,
          mistakes: 4,
          solvedCount: solvedGroups.length,
          completedAt: new Date().toISOString()
        });
      }
    }
  };

  // Connections: Shuffle remaining words
  const shuffleWords = () => {
    setRemainingWords([...remainingWords].sort(() => Math.random() - 0.5));
  };

  // Connections: Deselect all
  const deselectAll = () => {
    setSelectedWords([]);
  };

  // Connections: Create new puzzle
  const handleCreatePuzzle = async (puzzleData) => {
    const puzzlesRef = ref(database, 'puzzles/connections');
    const newPuzzleRef = push(puzzlesRef);

    await set(newPuzzleRef, {
      ...puzzleData,
      createdBy: currentUser.uid,
      creatorName: userProfile?.name || 'Unknown',
      createdAt: new Date().toISOString(),
      forPartner: true
    });

    setShowCreateForm(false);
  };

  useEffect(() => {
    if (activeTab === 'wordle') {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [currentGuess, wordleComplete, activeTab]);

  // Load selected puzzle
  useEffect(() => {
    if (selectedPuzzle) {
      const allWords = selectedPuzzle.groups.flatMap(g => g.words);
      setRemainingWords([...allWords].sort(() => Math.random() - 0.5));
      setSolvedGroups([]);
      setSelectedWords([]);
      setMistakes(0);
      setConnectionsComplete(false);
      setHintRevealed(false);
      // Check if user has failed this puzzle before
      setHasFailedBefore(selectedPuzzle.myProgress?.failed === true);
    }
  }, [selectedPuzzle]);

  const getGroupColor = (index) => {
    const colors = [
      'rose-light',    // Light rose/pink
      'rose-medium',   // Medium rose
      'rose-deep',     // Deep rose
      'rose-purple'    // Purple-ish rose
    ];
    return colors[index % colors.length];
  };

  // Filter puzzles based on selected filter
  const getFilteredPuzzles = () => {
    if (puzzleFilter === 'all') {
      return connectionsPuzzles;
    } else if (puzzleFilter === 'henry') {
      return connectionsPuzzles.filter(p =>
        p.creatorName?.toLowerCase().includes('henry')
      );
    } else if (puzzleFilter === 'omi') {
      return connectionsPuzzles.filter(p =>
        p.creatorName?.toLowerCase().includes('omi')
      );
    } else if (puzzleFilter === 'unattempted') {
      return connectionsPuzzles.filter(p => !p.myProgress?.completed);
    }
    return connectionsPuzzles;
  };

  const filteredPuzzles = getFilteredPuzzles();

  return (
    <div className={`puzzle-page ${activeTab === 'connections' ? 'connections-theme' : ''}`}>
      {/* Floating watercolor orbs */}
      <div className="watercolor-orb orb-1"></div>
      <div className="watercolor-orb orb-2"></div>
      <div className="watercolor-orb orb-3"></div>
      <div className="watercolor-orb orb-4"></div>

      {/* Watercolor background gradients */}
      <div className="watercolor-gradient"></div>
      <div className="flowing-edge-top"></div>
      <div className="flowing-edge-bottom"></div>

      {/* Return to Desk Button */}
      <motion.button
        className="puzzle-return-button"
        onClick={() => navigate('/home#desk')}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        whileHover={{ scale: 1.05, x: 5 }}
        whileTap={{ scale: 0.95 }}
      >
        ← Return to Desk
      </motion.button>

      {/* Page Header */}
      <motion.div
        className="puzzle-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1 className="puzzle-title">Puzzles</h1>
        <p className="puzzle-subtitle">Let's play togeather :)</p>
      </motion.div>

      {/* Tab Navigation */}
      <motion.div
        className="puzzle-tabs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.8 }}
      >
        <button
          className={`puzzle-tab ${activeTab === 'wordle' ? 'active' : ''}`}
          onClick={() => setActiveTab('wordle')}
        >
          Daily Wordle
        </button>
        <button
          className={`puzzle-tab ${activeTab === 'connections' ? 'active' : ''}`}
          onClick={() => setActiveTab('connections')}
        >
          Connections
        </button>
      </motion.div>

      {/* Puzzle Content */}
      <motion.div
        className="puzzle-content"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        {activeTab === 'wordle' ? (
          <div className="wordle-container">
            {/* Partner Status - Real-time */}
            {partnerGuesses.length > 0 && !partnerCompleted && (
              <motion.div
                className="partner-status live"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <span className="pulse-dot"></span>
                <span>{partnerName} is playing... ({partnerGuesses.length}/6 guesses)</span>
              </motion.div>
            )}

            {partnerCompleted && (
              <motion.div
                className="partner-status completed"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <span className="flower-icon">🌸</span>
                <span>{partnerName} completed today's puzzle!</span>
              </motion.div>
            )}

            {/* Comparison View */}
            {showComparison && (
              <motion.div
                className="comparison-view"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <h3>Today's Results</h3>
                <div className="comparison-grid">
                  <div className="comparison-player">
                    <h4>{userProfile?.name || 'You'}</h4>
                    <p className={wordleWon ? 'won' : 'lost'}>
                      {wordleWon ? `${wordleGuesses.length}/6 ✓` : 'X/6'}
                    </p>
                    <div className="mini-grid">
                      {wordleGuesses.map((guess, i) => (
                        <div key={i} className="mini-row">
                          {guess.split('').map((_, j) => (
                            <div key={j} className={`mini-tile ${getTileColor(guess, j)}`}></div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="comparison-player">
                    <h4>{partnerName}</h4>
                    <p className={partnerWon ? 'won' : 'lost'}>
                      {partnerWon ? `${partnerGuesses.length}/6 ✓` : 'X/6'}
                    </p>
                    <div className="mini-grid">
                      {partnerGuesses.map((guess, i) => (
                        <div key={i} className="mini-row">
                          {guess.split('').map((_, j) => (
                            <div key={j} className={`mini-tile ${getTileColor(guess, j)}`}></div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Wordle Grid */}
            <div className="wordle-grid">
              {[...Array(6)].map((_, rowIndex) => (
                <div key={rowIndex} className="wordle-row">
                  {[...Array(5)].map((_, colIndex) => {
                    const guess = wordleGuesses[rowIndex];
                    const letter = guess ? guess[colIndex] : (rowIndex === wordleGuesses.length ? currentGuess[colIndex] : '');
                    const tileClass = guess ? getTileColor(guess, colIndex) : '';

                    return (
                      <motion.div
                        key={colIndex}
                        className={`wordle-tile ${tileClass} ${letter ? 'filled' : ''}`}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: guess ? colIndex * 0.1 : 0 }}
                      >
                        {letter}
                      </motion.div>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* Input Instructions */}
            {!wordleComplete && (
              <div className="wordle-instructions">
                <p className="current-guess-display">{currentGuess || '\u00A0'}</p>
              </div>
            )}

            {/* Virtual Keyboard */}
            <div className="virtual-keyboard">
              {KEYBOARD_ROWS.map((row, rowIndex) => (
                <div key={rowIndex} className="keyboard-row">
                  {row.map((key) => (
                    <motion.button
                      key={key}
                      className={`keyboard-key ${key.length > 1 ? 'wide' : ''} ${keyboardColors[key] || ''}`}
                      onClick={() => handleKeyboardClick(key)}
                      disabled={wordleComplete}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {key}
                    </motion.button>
                  ))}
                </div>
              ))}
            </div>

            {/* Game Over Message */}
            {wordleComplete && (
              <motion.div
                className="game-over-message"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h3>{wordleWon ? 'Yay!' : 'Oh no... you should have used TEAMWORK!'}</h3>
                <p>The word was: <strong>{wordleWord}</strong></p>
              </motion.div>
            )}
          </div>
        ) : (
          <div className="connections-container">
            <h2 className="connections-title">Connections</h2>
            <p className="connections-subtitle">Do you know how I think? Try and stump me!</p>

            {showCreateForm ? (
              <CreatePuzzleForm
                onClose={() => setShowCreateForm(false)}
                onSave={handleCreatePuzzle}
                currentUser={currentUser}
                userProfile={userProfile}
              />
            ) : !selectedPuzzle ? (
              <div className="connections-menu">
                <button
                  className="create-puzzle-btn"
                  onClick={() => setShowCreateForm(true)}
                >
                  + Create New Puzzle
                </button>

                <div className="puzzles-list">
                  <h3>Available Puzzles</h3>

                  {/* Filter Buttons */}
                  <div className="puzzle-filters">
                    <button
                      className={`filter-btn ${puzzleFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setPuzzleFilter('all')}
                    >
                      All
                    </button>
                    <button
                      className={`filter-btn ${puzzleFilter === 'henry' ? 'active' : ''}`}
                      onClick={() => setPuzzleFilter('henry')}
                    >
                      By Henry
                    </button>
                    <button
                      className={`filter-btn ${puzzleFilter === 'omi' ? 'active' : ''}`}
                      onClick={() => setPuzzleFilter('omi')}
                    >
                      By Omi
                    </button>
                    <button
                      className={`filter-btn ${puzzleFilter === 'unattempted' ? 'active' : ''}`}
                      onClick={() => setPuzzleFilter('unattempted')}
                    >
                      Unattempted
                    </button>
                  </div>

                  {filteredPuzzles.length === 0 ? (
                    <p className="no-puzzles">
                      {connectionsPuzzles.length === 0
                        ? "No puzzles yet. Create one for your partner!"
                        : "No puzzles match this filter."}
                    </p>
                  ) : (
                    <div className="puzzle-cards-grid">
                      {filteredPuzzles.map(puzzle => (
                        <div key={puzzle.id} className="puzzle-card" onClick={() => setSelectedPuzzle(puzzle)}>
                          <h4>{puzzle.title || 'Untitled Puzzle'}</h4>
                          <p>By {puzzle.creatorName}</p>
                          <div className="puzzle-meta">4 groups • {puzzle.createdAt ? new Date(puzzle.createdAt).toLocaleDateString() : 'Recently'}</div>

                          {/* Show completion status for both users */}
                          <div className="puzzle-status">
                            <div className="user-status">
                              <span className="user-label">You:</span>
                              {puzzle.myProgress?.completed ? (
                                <span className={`status-detail ${puzzle.myProgress.failed ? 'failed' : 'completed'}`}>
                                  {puzzle.myProgress.failed ? (
                                    `${puzzle.myProgress.solvedCount || 0}/4 groups`
                                  ) : (
                                    `${puzzle.myProgress.mistakes || 0} ${puzzle.myProgress.mistakes === 1 ? 'mistake' : 'mistakes'}`
                                  )}
                                </span>
                              ) : (
                                <span className="status-detail not-attempted">Not played</span>
                              )}
                            </div>
                            <div className="user-status">
                              <span className="user-label">{puzzle.partnerName}:</span>
                              {puzzle.partnerProgress?.completed ? (
                                <span className={`status-detail ${puzzle.partnerProgress.failed ? 'failed' : 'completed'}`}>
                                  {puzzle.partnerProgress.failed ? (
                                    `${puzzle.partnerProgress.solvedCount || 0}/4 groups`
                                  ) : (
                                    `${puzzle.partnerProgress.mistakes || 0} ${puzzle.partnerProgress.mistakes === 1 ? 'mistake' : 'mistakes'}`
                                  )}
                                </span>
                              ) : (
                                <span className="status-detail not-attempted">Not played</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="connections-game">
                {/* Game header */}
                <div className="connections-game-header">
                  <button onClick={() => setSelectedPuzzle(null)} className="back-btn">
                    ← Back
                  </button>
                  <h3>{selectedPuzzle.title}</h3>
                  <p>Created by {selectedPuzzle.creatorName}</p>
                  <div className="mistakes-counter">
                    Mistakes: {mistakes}/4
                  </div>
                </div>

                {/* Solved groups */}
                <div className="solved-groups-container">
                  {solvedGroups.map((group, index) => {
                    const difficultyLabels = ['Easiest', 'Medium', 'Hard', 'Hardest'];
                    const difficultyLevel = group.difficulty !== undefined ? group.difficulty : index;

                    return (
                      <motion.div
                        key={index}
                        className={`solved-group ${getGroupColor(difficultyLevel)}`}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                      >
                        <div className="group-difficulty-label">{difficultyLabels[difficultyLevel]}</div>
                        <h4>{group.category}</h4>
                        <p>{group.words.join(', ')}</p>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Word grid */}
                {!connectionsComplete && (
                  <>
                    <div className="connections-grid">
                      {remainingWords.map((word, index) => (
                        <motion.button
                          key={word}
                          className={`connection-word ${selectedWords.includes(word) ? 'selected' : ''}`}
                          onClick={() => toggleWordSelection(word)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {word}
                        </motion.button>
                      ))}
                    </div>

                    {/* Controls */}
                    <div className="connections-controls">
                      <button onClick={shuffleWords}>Shuffle</button>
                      <button onClick={deselectAll} disabled={selectedWords.length === 0}>
                        Deselect All
                      </button>
                      <button
                        onClick={handleConnectionsGuess}
                        disabled={selectedWords.length !== 4}
                        className="submit-btn"
                      >
                        Submit
                      </button>
                    </div>

                    {/* Hint Section - shown after a previous failure */}
                    {selectedPuzzle.hint && hasFailedBefore && (
                      <motion.div
                        className="hint-section"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {!hintRevealed ? (
                          <button
                            className="reveal-hint-btn"
                            onClick={() => setHintRevealed(true)}
                          >
                            💡 Reveal Hint
                          </button>
                        ) : (
                          <div className="hint-content">
                            <span className="hint-label">Hint:</span>
                            <p>{selectedPuzzle.hint}</p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </>
                )}

                {/* Completion message */}
                {connectionsComplete && (
                  <motion.div
                    className="game-over-message"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {solvedGroups.length === selectedPuzzle.groups.length ? (
                      <>
                        <h3>Yay!</h3>
                        {selectedPuzzle.completionMessage && (
                          <p className="custom-message">{selectedPuzzle.completionMessage}</p>
                        )}
                      </>
                    ) : (
                      <>
                        <h3>💭 Better luck next time!</h3>
                        <p>You can try again later.</p>
                        {selectedPuzzle.hint && (
                          <p className="hint-available-notice">
                            💡 A hint will be available on your next attempt!
                          </p>
                        )}
                      </>
                    )}
                  </motion.div>
                )}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Puzzle;
